import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { setUnitPlurals } from '../lib/units';

export const UNIT_NAMES_PATH = '/admin/v1/inventory/unit-names';

export interface UnitName {
  unit: string;
  plural: string;
}

/**
 * Loads what more than one of each counting unit is called, once, into `lib/units.ts`.
 *
 * Mounted at the layout so it is fetched a single time per session and every screen beneath it
 * reads the answers synchronously — `formatQty` is called from twenty places, in tables, in
 * confirmation sentences and inside toast copy, and a formatter that takes a hook cannot be
 * called from most of them. The reasoning for a module-level registry rather than a parameter is
 * in `lib/units.ts`.
 *
 * There is nothing to do while it is in flight: an empty registry falls back to the singular,
 * which is the same reading a unit nobody has answered for gets, so a quantity is never wrong —
 * at worst it is briefly clumsy.
 */
export function useUnitNames(): void {
  const { data } = useQuery({
    queryKey: [UNIT_NAMES_PATH],
    queryFn: () => rawApi<UnitName[]>('GET', UNIT_NAMES_PATH),
    // A word's plural does not change while somebody is reading a report.
    staleTime: 10 * 60 * 1000,
  });
  if (data) setUnitPlurals(data);
}
